import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getBroker } from "../lib/api";
import { BrokerLogo } from "../lib/logo.jsx";
import { Seo } from "../components/Seo";
import { Phone, Mail, Globe, MapPin, Printer, ArrowLeft, ArrowUpRight, Hash, Star } from "lucide-react";

const shortName = (name) => name.replace(/MENKUL.*/i, "").replace("A.Ş.", "").trim();

const Field = ({ icon: Icon, label, value, href, testid }) => {
  if (!value) return null;
  const inner = (
    <>
      <Icon size={18} className="mt-0.5 shrink-0 text-claret" />
      <div className="min-w-0">
        <div className="kicker text-muted-foreground">{label}</div>
        <div className="mt-0.5 break-words text-sm font-medium text-ink">{value}</div>
      </div>
    </>
  );
  const base = "flex items-start gap-3 border border-hair bg-card p-4 transition-colors";
  return href ? (
    <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className={`${base} hover:border-ink`} data-testid={testid}>{inner}</a>
  ) : (
    <div className={base} data-testid={testid}>{inner}</div>
  );
};

const BrokerDetail = () => {
  const { slug } = useParams();
  const [broker, setBroker] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setBroker(null); setNotFound(false);
    getBroker(slug).then(setBroker).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-2xl font-semibold text-ink">Aracı kurum bulunamadı</h1>
        <Link to="/araci-kurumlar" className="mt-4 inline-flex items-center gap-2 text-claret"><ArrowLeft size={16} /> Tüm kurumlara dön</Link>
      </div>
    );

  if (!broker) return <div className="mx-auto max-w-5xl px-4 py-24 text-center text-muted-foreground" data-testid="broker-loading">Yükleniyor...</div>;

  const short = shortName(broker.name);
  const jsonLd = {
    "@context": "https://schema.org", "@type": "FinancialService",
    name: broker.name, url: broker.domain ? `https://${broker.domain}` : undefined,
    telephone: broker.phone || undefined, email: broker.email || undefined, faxNumber: broker.fax || undefined,
    address: broker.address ? { "@type": "PostalAddress", streetAddress: broker.address, addressCountry: "TR" } : undefined,
    identifier: broker.mersis || undefined, areaServed: "TR",
  };

  return (
    <div className="paper-texture">
      <Seo title={`${short} | İletişim Bilgileri & SPK Aracı Kurum`}
        description={`${short} iletişim bilgileri: telefon, faks, e-posta, adres ve web sitesi. SPK lisanslı aracı kurum rehberi.`}
        path={`/kurum/${broker.slug}`} jsonLd={jsonLd} />
      <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10">
        <Link to="/araci-kurumlar" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-ink" data-testid="back-to-brokers">
          <ArrowLeft size={16} /> Tüm Aracı Kurumlar
        </Link>

        <div className="mt-6 flex flex-col gap-5 border-2 border-ink bg-card p-6 sm:flex-row sm:items-center sm:p-8" data-testid="broker-detail-header">
          <BrokerLogo slug={broker.slug} name={broker.name} size={88} />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="font-display text-3xl font-semibold tracking-tight text-ink">{short}</h1>
              {broker.is_popular && <Star size={20} className="text-claret" fill="currentColor" />}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{broker.name}</p>
            {broker.tagline && <span className="kicker mt-3 inline-block border-l-2 border-claret pl-2.5 text-claret">{broker.tagline}</span>}
          </div>
          {broker.cta_url && (
            <a href={broker.cta_url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-1.5 rounded-sm bg-ink px-6 py-3 text-sm font-semibold text-paper transition-colors hover:bg-claret" data-testid="broker-cta-button">
              {broker.is_popular ? broker.cta_text : "Resmi Site"} <ArrowUpRight size={15} />
            </a>
          )}
        </div>

        <h2 className="mt-10 border-b border-ink pb-2 font-display text-xl font-semibold text-ink">İletişim Bilgileri</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <Field icon={Phone} label="Telefon" value={broker.phone} href={broker.phone ? `tel:${broker.phone.replace(/\s/g, "")}` : null} testid="detail-phone" />
          <Field icon={Printer} label="Faks" value={broker.fax} testid="detail-fax" />
          <Field icon={Mail} label="E-posta" value={broker.email} href={broker.email ? `mailto:${broker.email}` : null} testid="detail-email" />
          <Field icon={Globe} label="Web Sitesi" value={broker.website} href={broker.domain ? `https://${broker.domain}` : null} testid="detail-website" />
          <Field icon={Hash} label="MERSİS No" value={broker.mersis} testid="detail-mersis" />
          <Field icon={MapPin} label="Genel Müdürlük Adresi" value={broker.address} testid="detail-address" />
        </div>

        <div className="mt-8 border-l-2 border-claret bg-tint/60 p-5 text-xs leading-relaxed text-muted-foreground" data-testid="detail-disclaimer">
          Bu kuruma ait iletişim bilgileri KAP, SPK duyuruları ve resmi kurum kanallarından derlenmiştir.
          Bilgiler değişkenlik gösterebilir; işlem yapmadan önce kurumun resmi web sitesinden teyit ediniz.
          İçerik yatırım tavsiyesi değildir.
        </div>
      </div>
    </div>
  );
};

export default BrokerDetail;
